import React, { useRef, useEffect, useState } from "react";
import GameCard from "../gamecard/gameCard.jsx";
import { useNavigate } from "react-router-dom";
import slugify from "../../utils/slugify";
import { useTheme } from "../../context/ThemeContext";
import { getThemeClasses } from "../utils/themeClass";

const ScrollableCategory = ({ category, games }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  const scrollRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const clones = 1;  // how many full clones (middle + before + after)
  const minCardsToLoop = 5;  // minimum needed to enable loop
  const pauseDuration = 2000;
  const scrollDuration = 500;

  const shouldLoop = games.length >= minCardsToLoop;

  // ⏩ Triple list: [CLONES] [REAL] [CLONES]
  const fullList = shouldLoop ? [...games, ...games, ...games] : games;

  // 🏁 On mount: measure & position to middle REAL section
  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider) return;

    const firstCard = slider.querySelector(".gameCardClass");
    if (firstCard) {
      const style = getComputedStyle(firstCard);
      const marginRight = parseFloat(style.marginRight) || 0;
      const width = firstCard.offsetWidth + marginRight;
      setCardWidth(width);

      if (shouldLoop) {
        slider.scrollLeft = width * games.length; // scroll to middle real section
      } else {
        slider.scrollLeft = 0;
      }
    }
  }, [games, shouldLoop]);

  // 🔁 Smooth auto-scroll if loop enabled
  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider || !cardWidth || !shouldLoop) return;

    let animFrame;
    let timeout;

    const easeInOutQuad = (t) =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const smoothScroll = () => {
      if (isDragging) return;

      const start = slider.scrollLeft;
      const target = start + cardWidth;
      const realWidth = cardWidth * games.length;

      const duration = scrollDuration;
      const startTime = performance.now();

      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeInOutQuad(progress);

        slider.scrollLeft = start + (target - start) * eased;

        if (progress < 1) {
          animFrame = requestAnimationFrame(animate);
        } else {
          // If in trailing clones, jump back to same spot in real middle
          if (slider.scrollLeft >= realWidth * 2) {
            slider.scrollLeft = slider.scrollLeft - realWidth;
          }
          timeout = setTimeout(smoothScroll, pauseDuration);
        }
      };

      animFrame = requestAnimationFrame(animate);
    };

    timeout = setTimeout(smoothScroll, pauseDuration);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(animFrame);
    };
  }, [cardWidth, isDragging, games, shouldLoop]);

  // 🖱️ Drag to scroll
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.pageX || e.touches[0].pageX);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const slider = scrollRef.current;
    const x = e.pageX || e.touches[0].pageX;
    const walk = (x - startX) * 1.5;
    slider.scrollLeft = scrollLeft - walk;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleCategoryClick = () => {
    const slug = slugify(category.title);
    navigate(`/games/${slug}?c=${category.id}`);
  };

  return (
    <div className="mb-8 px-2 md:px-4">
      {/* Header */}
      <div className={`flex justify-between items-center mb-4 p-3 rounded-xl border shadow-md ${themeClass.overlayBg} ${themeClass.borderColor}`}>
        <h2 className={`text-lg md:text-xl font-bold uppercase ${themeClass.titleColor}`}>
          {category.title}
        </h2>
        <button
          onClick={handleCategoryClick}
          className={`px-4 py-1 text-sm md:text-base font-semibold rounded-full transition shadow hover:shadow-lg transform hover:scale-105 ${themeClass.exploreBtn}`}
        >
          Explore &raquo;
        </button>
      </div>

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className={`flex space-x-4 px-2 py-4 rounded-xl border shadow-sm cursor-grab active:cursor-grabbing transition-transform hover:scale-[1.01] overflow-x-auto scrollbar-none ${themeClass.scrollContainerBg} ${themeClass.borderColor} ${themeClass.hoverBorder}`}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {fullList.map((game, i) => (
          <div key={`card-${i}`} className="gameCardClass flex-shrink-0">
            <GameCard game={game} index={i} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScrollableCategory;
